import { useCallback, useContext } from 'react';
import { match } from 'ts-pattern';
import { GlobalTextVariablesContext } from '../../../contexts/global-text-variables-context';
import { getActiveCategories } from '../../dotcms-category';
import type { IEsppResourcesPageData } from '../dotcms-espp-resources-page';

/** These string values must stay the same as the keys in dotcms, don't change them! */
export enum ResourceSectionCategory {
  OVERVIEW = 'Overview',
  TRAINING = 'Training',
  TAX = 'Tax',
  MISC = 'Misc.',
  /** PLAN_DOCUMENTS isn't from dotcms, but is a pure frontend thing
   * (we get these from the backend instead of CMS) */
  PLAN_DOCUMENTS = 'PLAN_DOCUMENTS',
}

export type IResourceLibraryCategorizableResource = {
  resourcePageSectionCategory: ResourceSectionCategory;
};

export const getResourceSectionCategoryFromDotcms = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resourceSectionCategory: any,
) => {
  const resourceSectionCategoriesFromDotcms = resourceSectionCategory
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    getActiveCategories(resourceSectionCategory)
    : [];

  if (
    !resourceSectionCategoriesFromDotcms ||
    resourceSectionCategoriesFromDotcms.length === 0 ||
    typeof resourceSectionCategoriesFromDotcms[0] !== 'string'
  ) {
    return ResourceSectionCategory.MISC;
  }

  const maybeMatchingEnumKey: ResourceSectionCategory | undefined =
    Object.values(ResourceSectionCategory).find(
      (thisResourceSectionCategory) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
        thisResourceSectionCategory === resourceSectionCategoriesFromDotcms[0],
    );

  return maybeMatchingEnumKey ?? ResourceSectionCategory.MISC;
};

export const useTranslateResourceSectionCategory = (
  data: Pick<Partial<IEsppResourcesPageData>, 'planDocumentsHeading'>,
) => {
  const globalTextVariables = useContext(GlobalTextVariablesContext);
  const translateResourceSectionCategory = useCallback(
    (resourceType: ResourceSectionCategory) =>
      match({ resourceType })
        .with(
          { resourceType: ResourceSectionCategory.OVERVIEW },
          () => globalTextVariables.articleCategorySectionHeadingOverview,
        )
        .with(
          { resourceType: ResourceSectionCategory.MISC },
          () => globalTextVariables.articleCategorySectionHeadingMisc,
        )
        .with(
          { resourceType: ResourceSectionCategory.TAX },
          () => globalTextVariables.articleCategorySectionHeadingTax,
        )
        .with(
          { resourceType: ResourceSectionCategory.TRAINING },
          () => globalTextVariables.articleCategorySectionHeadingTraining,
        )
        .with(
          { resourceType: ResourceSectionCategory.PLAN_DOCUMENTS },
          () => data.planDocumentsHeading,
        )
        .exhaustive(),
    [
      globalTextVariables.articleCategorySectionHeadingOverview,
      globalTextVariables.articleCategorySectionHeadingMisc,
      globalTextVariables.articleCategorySectionHeadingTax,
      globalTextVariables.articleCategorySectionHeadingTraining,
      data.planDocumentsHeading,
    ],
  );
  return { translateResourceSectionCategory };
};
